import { getCollabKey } from "@/api/services";
import type { CollabKey } from "@/api/types";

/**
 * Keys for the collab server, one per article (OBS-1974). digifirst-cms hands
 * out a 15-minute key that opens that article's document and nothing else; it
 * replaces the single key that used to be built into the app and opened every
 * document.
 *
 * A key is reused (on reconnect, or by a second connection to the same article)
 * until 5 minutes before it expires, so a collab restart doesn't send every
 * editor to digifirst-cms at once.
 *
 * The expiry limits reconnects only. The collab server checks a key when a
 * connection authenticates, so a connection that is already open keeps its
 * access after the key expires, or after the user's access changes.
 *
 * Renewal is timed from the key's lifetime (`expiresIn`, from the server),
 * counted from when the request was sent, on this device's clock. Comparing the
 * server's `expiresAt` with this device's clock would reuse expired keys on a
 * device whose clock runs slow.
 */
export const RENEW_BEFORE_EXPIRY_MS = 5 * 60_000;

type Entry = {
  key: Promise<CollabKey>;
  /** When the request was sent (this device's clock). */
  sentAt: number;
  /** When to ask for a new key (this device's clock); +Infinity until the request answers. */
  renewAt: number;
};

export type CollabKeys = {
  /** This article's key: the cached one, or a fresh one near expiry. */
  key: (articleId: string) => Promise<CollabKey>;
  /** Just the token of `key`, for a provider's `token` option. */
  token: (articleId: string) => Promise<string>;
  /** Forget this article's key, e.g. after the collab server refused it. */
  invalidate: (articleId: string) => void;
  /** Forget every key, e.g. on logout. */
  clear: () => void;
};

/** The key's lifetime in ms, from the server's `expiresIn`; 0 (use once) if it's missing. */
const lifetimeMs = (key: CollabKey) =>
  typeof key.expiresIn === "number" && Number.isFinite(key.expiresIn) && key.expiresIn > 0
    ? key.expiresIn * 1000
    : 0;

export const createCollabKeys = (
  fetchKey: (articleId: string) => Promise<CollabKey> = getCollabKey,
  now: () => number = Date.now,
): CollabKeys => {
  const entries = new Map<string, Entry>();

  const key = async (articleId: string) => {
    let entry = entries.get(articleId);
    if (!entry || now() >= entry.renewAt) {
      entry = { key: fetchKey(articleId), sentAt: now(), renewAt: Number.POSITIVE_INFINITY };
      entries.set(articleId, entry);
    }
    const pending = entry;
    try {
      const answer = await pending.key;
      pending.renewAt = pending.sentAt + lifetimeMs(answer) - RENEW_BEFORE_EXPIRY_MS;
      return answer;
    } catch (e) {
      // A failed request isn't cached: the next connection attempt asks again.
      if (entries.get(articleId) === pending) entries.delete(articleId);
      throw e;
    }
  };

  return {
    key,
    token: async (articleId) => (await key(articleId)).token,
    invalidate: (articleId) => {
      entries.delete(articleId);
    },
    clear: () => {
      entries.clear();
    },
  };
};

/** The app's keys, shared by the editor's connection and the Google Docs import wait. */
export const collabKeys = createCollabKeys();
